<template>
  <div
    class="product-scroller-wrapper relative my-0 transition-all"
    :class="{ 'is-edit-mode': isMagnoliaEdit }"
  >
    <section
      ref="root"
      class="product-scroller reset-container-padding"
      data-productscroller="root"
    >
      <div class="product-scroller__inner">
        <div data-productscroller="sticky-content">
          <h2 class="sr-only">{{ srHeadline }}</h2>
          <p v-if="srGraphicDescription" class="sr-only">{{ srGraphicDescription }}</p>
          <div class="product-scroller__container" data-productscroller="container">
            <div class="product-scroller__media">
              <figure class="image product-scroller__image">
                <PictureElement :img="posterImage" img-class="w-full h-full object-contain" />
              </figure>
              <canvas aria-hidden="true" data-productscroller-canvas></canvas>
            </div>
            <ul class="product-scroller__features container flex flex-col">
              <li
                v-for="feature in mapNodes(features)"
                :key="getId(feature)"
                class="product-scroller__feature flex justify-end transition-all"
                :data-productscroller-timing="feature.startTime"
                tabindex="-1"
              >
                <div
                  class="flex w-8/12 flex-shrink-0 gap-4 rounded-xl bg-blue-950 p-2 text-white md:p-4 lg:w-6/12 xl:w-4/12"
                >
                  <div
                    class="aspect-square h-12 flex-shrink-0 rounded-full p-2 md:h-16"
                    :class="contentToTailwindClasses({ backgroundColor: feature.iconBgColor })"
                  >
                    <PictureElement :img="feature.icon" img-class="object-contain " />
                  </div>
                  <div class="w-full text-white">
                    <h3 class="mb-2 font-bold xl:text-xl" data-productscroller="sr-item">
                      {{ feature.title }}
                    </h3>
                    <div class="prose prose-white" v-html="feature.description"></div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <CallToActionComponent
          v-if="containsLink(callToAction)"
          :link="callToAction"
          class="product-scroller__button"
          data-productscroller="button"
          data-productscroller-active="false"
          :custom-tracking-event="trackingEvent"
        ></CallToActionComponent>
      </div>
    </section>
  </div>
</template>
<script setup lang="ts">
import CallToActionComponent from "~/templates/components/cms/CallToActionComponent.vue";
import PictureElement from "~/templates/elements/PictureElement.vue";
import { ProductScroller } from "./utils/product-scroller";
import "./ProductScroller.css";
import { getId, mapNodes, containsLink } from "~/utils/helper/magnolia";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { contentToTailwindClasses } from "~/utils/helper/tailwind";

const trackingEvent = "{ event: 'gx.shop_now', event_name: 'shop_now' }";

const { isMagnoliaEdit } = useMagnoliaContext();
const {
  public: { mgnlAssetHost },
} = useRuntimeConfig();

export interface ProductScrollerProps {
  srHeadline: string;
  srGraphicDescription?: number;
  posterImage?: object;
  callToAction?: object;
  features?: {
    title: string;
    description: string;
    startTime: number;
    icon: object;
    iconBgColor: string;
  }[];
}
defineProps<ProductScrollerProps>();
defineOptions({
  inheritAttrs: false,
});

const root = ref(null);

onMounted(() => {
  const { width, height } = useWindowSize();
  const $rootAPI = new ProductScroller(root.value, {
    baseUrl: mgnlAssetHost,
  });
  $rootAPI.initialize();
  watch([width, height], () => {
    $rootAPI.setupCanvas();
    $rootAPI.renderFrame();
    ScrollTrigger.refresh();
  });
});
</script>

<!-- Cropping the whitespace Animation has at the beginning and the end of the product scroller on initialized animation when it is not in edit mode -->
<style scoped>
.product-scroller-wrapper:not(.is-edit-mode) {
  @apply my-[-20lvh];
  .product-scroller__image {
    @apply my-[20lvh];
  }
}
</style>
